import {
  Button,
  Card,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FontAwesomeIcon,
  Header,
  NotificationHub,
  RichTable,
  Section,
  SectionBody,
  SectionHeader
} from "@nef/core"
import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { AppDispatch } from "../../app/store"
import { fetchInstrumentObjectById, fetchSecurityLookUp, setSelectedInstrument } from "../../redux/instrument/actions"
import InstrumentSearch from "../../components/common/InstrumentSearch"
import SearchableIssuer from "../../components/common/SearchableIssuer"
import { IssuerModel } from "../../generated/api"
import useGetUserAndRole from "../../hooks/useGetUserAndRole"
import { FileExtensions, InstrumentStatusEnum, ServiceCallReportTypes, USER_ROLES } from "../../types/_enums"
import TableDownloadDropdown from "../../components/common/TableDownloadDropdown"
import { downloadCSVFromString } from "../../utils/csvHandler"
import { HEADERS } from "../../constants/common"
import useGetColumnMap from "../../hooks/useGetColumnMap"
import securityLookUpColumns from "../../constants/table/symbolDirectoryColumns"

const SymbolDirectory = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [userRole]: any[] = useGetUserAndRole()

  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState(null)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const [distribution, setDistribution] = useState<string>("")
  const [issuer, setIssuer] = useState<IssuerModel | null>(null)
  const [instrument, setInstrument] = useState<any>(null)
  const [lookUpList, setLookUpList] = useState([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFileDownloading, setIsFileDownloading] = useState<boolean>(false)
  const [hiddenColumns, updateLocalStorage, handleColumnSelection] = useGetColumnMap("lookuplist")

  const list = Object.keys(HEADERS)

  useEffect(() => {
    return updateLocalStorage
  }, [])

  const dropDownToggler = () => {
    setIsDropDownOpen((prevIsOpen: boolean) => !prevIsOpen)
  }

  const onInstrumentSelect = async ({
    row: {
      original: { id }
    }
  }: any) => {
    try {
      setIsLoading(true)
      const res = await dispatch(fetchInstrumentObjectById(id)).unwrap()
      await dispatch(setSelectedInstrument(res))
      setInstrument(res)
    } catch (error: any) {
      NotificationHub.send("danger", `${error.status} ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const clearInstrument = async () => {
    setInstrument(null)
    await dispatch(setSelectedInstrument(null))
  }

  const clearIssuer = async () => {
    setIssuer(null)
  }

  const onDownload = async (reportName: ServiceCallReportTypes) => {
    if (!selectedDownloadFormat) {
      NotificationHub.send("danger", `Please select a format to download!`)
      return
    }
    // const payload = {
    //   requestHeaders: HEADERS[selectedDownloadFormat],
    //   reportName
    // }
    const payload = {
      excludeFromDistribution: String(distribution === "excluded"),
      symbol: instrument?.symbol,
      fkissuerId: issuer?.issuerId && String(issuer?.issuerId)
    }
    const requestHeaders = HEADERS[selectedDownloadFormat]

    try {
      setIsLoading(true)
      const data = await dispatch(fetchSecurityLookUp({ payload, requestHeaders })).unwrap()
      // const data = await dispatch(fetchReportData(payload)).unwrap()
      if (!data) {
        NotificationHub.send("primary", `No data available to download!`)
        setIsLoading(false)
        return
      }
      downloadCSVFromString(data, `symbol-directory.${selectedDownloadFormat}`, selectedDownloadFormat)
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      NotificationHub.send("danger", `${error.message} ${JSON.stringify(error.errors)}`)
    }
  }

  const onQuery = async () => {
    try {
      if (userRole.includes(USER_ROLES.SUPERVISOR) && !distribution) {
        NotificationHub.send("danger", `Please select excluded from distribution.`)
        return
      }

      if (!instrument?.symbol && !issuer?.issuerId) {
        NotificationHub.send("danger", `Please select an instrument or issuer.`)
        return
      }

      setIsLoading(true)
      const payload = {
        excludeFromDistribution: String(distribution === "excluded"),
        symbol: instrument?.symbol,
        fkIssuerId: issuer?.issuerId && String(issuer?.issuerId)
      }
      const response = await dispatch(fetchSecurityLookUp({ payload })).unwrap()
      if (response?.length) {
        setLookUpList(response)
      }
    } catch (error: any) {
      NotificationHub.send("danger", `${error?.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadButtonClick = async (type: FileExtensions) => {
    if (userRole.includes(USER_ROLES.SUPERVISOR) && !distribution) {
      NotificationHub.send("danger", `Please select excluded from distribution.`)
      return
    }

    if (!instrument?.symbol && !issuer?.issuerId) {
      NotificationHub.send("danger", `Please select an instrument or issuer.`)
      return
    }

    if (lookUpList?.length) {
      const payload = {
        excludeFromDistribution: String(distribution === "excluded"),
        symbol: instrument?.symbol,
        fkissuerId: issuer?.issuerId && String(issuer?.issuerId)
      }

      const requestHeaders = HEADERS[type]
      try {
        setIsFileDownloading(true)
        const response = await dispatch(fetchSecurityLookUp({ payload, requestHeaders })).unwrap()
        if (lookUpList) {
          downloadCSVFromString(response, `security-lookup.${type}`, type)
        }
        setIsFileDownloading(false)
      } catch (error: any) {
        setIsFileDownloading(false)
        NotificationHub.send("danger", `${error.status} ${error.message}`)
      }
    }
  }

  const onSymbolClick = (data: any) => {
    const appid = data?.status === InstrumentStatusEnum.LIVE || InstrumentStatusEnum.DELETE ? data?.instrumentId : data?.instrumentStageId
    window.open(`/instrument_manager?instrumentMode=View&nsm=${data?.nsm}&appId=${appid}`, "_blank")
  }

  return (
    <>
      <Section className="symbol-dir__container">
        <SectionBody>
          <div className="symbol-dir__download-wrapper">
            <div className="symbol-dir__download">
              {userRole.includes(USER_ROLES.SUPERVISOR) && (
                <div className="symbol-dir__distribution">
                  <Header size={2}>Excluded From Distribution</Header>
                  <div className="symbol-dir__select">
                    <input
                      type="radio"
                      checked={distribution === "included"}
                      onChange={(e) => {
                        setDistribution("included")
                      }}
                      data-testid='included'
                    />
                    <p>Included</p>
                    <input
                      type="radio"
                      checked={distribution === "excluded"}
                      onChange={(e) => {
                        setDistribution("excluded")
                      }}
                      data-testid='excluded'
                    />
                    <p>Excluded</p>
                  </div>
                </div>
              )}
              <Header size={2}>Instrument Directory Download</Header>
              <div className="symbol-dir__instrument">
                <Dropdown isOpen={isDropDownOpen} toggle={dropDownToggler}>
                  <DropdownToggle className="dropdown-selector__toggle">
                    <p data-testid='downloadFormatDropdown'>{selectedDownloadFormat || "Select format"}</p>
                    <FontAwesomeIcon className="fa-solid fa-angle-down" />
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-selector__menu">
                    {!!list?.length &&
                      list.map((item: any) => {
                        return (
                          <DropdownItem
                            className="dropdown-selector__item"
                            key={item}
                            onClick={() => {
                              setSelectedDownloadFormat(item)
                              setIsDropDownOpen(false)
                            }}
                          >
                            {item?.toUpperCase()}
                          </DropdownItem>
                        )
                      })}
                  </DropdownMenu>
                </Dropdown>
                <Button
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={() => {
                    onDownload(ServiceCallReportTypes.INSTRUMENT_DIRECTORY)
                  }}
                  data-testid='downloadButton'
                >
                  Download
                </Button>
              </div>
              <Header size={2}>Security Look-Up</Header>
              {!instrument?.symbol ? (
                <div className="symbol-dir__inst-wrapper">
                  <InstrumentSearch onInstrumentSelect={onInstrumentSelect} />
                </div>
              ) : (
                <div className="symbol-dir__label">
                  <Header size={5}>Symbol:</Header>
                  <p>{instrument?.symbol || ""}</p>
                  <Button onClick={clearInstrument} size="sm" color="danger">
                    Clear Instrument
                  </Button>
                </div>
              )}
              {!instrument?.symbol &&
                (!issuer?.issuerId ? (
                  <div className="symbol-dir__issuer-wrapper">
                    <SearchableIssuer
                      isOptional
                      setSelectedIssuer={setIssuer}
                      showOnlyActive
                      isAdmin
                      isPrice
                    />
                  </div>
                ) : (
                  <div className="symbol-dir__label">
                    <Header size={5}>Issuer Name:</Header>
                    <p>{issuer?.issuerName || ""}</p>
                    <Button onClick={clearIssuer} size="sm" color="danger">
                      Clear Issuer
                    </Button>
                  </div>
                ))}
              <div className="symbol-dir__query-btn" data-testid="queryBtn">
                <Button onClick={onQuery} disabled={isLoading} isLoading={isLoading}>
                  Query
                </Button>
              </div>
            </div>
          </div>

          <div>
            {!!lookUpList?.length && (
              <div>
                <RichTable
                  bodyHeight={300}
                  options={{
                    columns: securityLookUpColumns(onSymbolClick, lookUpList),
                    data: lookUpList,
                    initialState: {
                      hiddenColumns
                    }
                  }}
                  onShowHideColumns={handleColumnSelection}
                  loading={isLoading}
                  rowBgPattern="border"
                  isColumnSelectorEnabled
                >
                  {!isLoading && <Header size={4}>{`Total Count : ${lookUpList?.length}`}</Header>}
                  <TableDownloadDropdown onDownloadClick={handleDownloadButtonClick} isFileDownloading={isFileDownloading} />
                </RichTable>
              </div>
            )}
          </div>
        </SectionBody>
      </Section>
      <NotificationHub />
    </>
  )
}

export default SymbolDirectory
